import { useMemo } from 'react';

import { useOverviewRowsBuilder } from '../../../utils/appointments/useOverviewRowsBuilder';
import {
  INLINE_TYPES,
  VIRTUAL_VISIT_TYPE_CODE,
  FORBIDDEN_VISIT_TYPES_FOR_MAP,
  TELEMEDICINE_VISIT_TYPE_CODE,
  HOLD_MY_SPOT_VISIT_TYPE_CODE,
  OFFICE_VISIT_TYPE_CODE,
  URGENT_CARE_TYPE_CODE,
} from '../../../constants';

export function useOverviewRows({
  price,
  reason,
  service,
  provider,
  location,
  appointment,
  formatMessage,
  visitType: { code },
  additionalInfoFormData,
  schedule: { time, location: selfSchedulingLocation },
}) {
  const { consentsAssigment, location: appointmentLocation = {} } = appointment;

  const isVirtualVisit = code === VIRTUAL_VISIT_TYPE_CODE;

  const isEirTelemedicineVisit = code === TELEMEDICINE_VISIT_TYPE_CODE;

  const isEirHotMySpotVisit = code === HOLD_MY_SPOT_VISIT_TYPE_CODE;

  const isInlineVisit = INLINE_TYPES.includes(code);

  const isOfficeVisit = code === OFFICE_VISIT_TYPE_CODE;

  const isUrgentCareVisit = code === URGENT_CARE_TYPE_CODE;

  const showAdditionalInfo =
    additionalInfoFormData &&
    (isOfficeVisit || isVirtualVisit || isUrgentCareVisit);

  const { geofenceLat, geofenceLng } = { ...appointmentLocation };

  const isForbiddenMap =
    FORBIDDEN_VISIT_TYPES_FOR_MAP.includes(code) ||
    !(geofenceLat && geofenceLng);

  const {
    makeDateRow,
    makeReasonRow,
    makeConsentRow,
    makePaymentRow,
    makeLocationRow,
    makeProviderRow,
    makeDateAndTimeRow,
  } = useOverviewRowsBuilder({ formatMessage, isInlineVisit });

  const DATE_ROW = useMemo(() => makeDateRow({ time }), [time]);

  const REASON_ROW = useMemo(() => makeReasonRow(reason), [reason]);

  const PROVIDER_ROW = useMemo(
    () =>
      makeProviderRow({
        provider: { ...provider, location: selfSchedulingLocation },
        service,
        hasLocation: true,
        isVirtualVisit,
        isForbiddenMap,
      }),
    [provider, selfSchedulingLocation, isVirtualVisit, isForbiddenMap],
  );

  const PAYMENT_ROW = useMemo(() => makePaymentRow(price), [price]);

  const CONSENT_ROW = useMemo(
    () =>
      makeConsentRow({
        appointment,
        providerContext: false,
      }),
    [appointment, consentsAssigment],
  );

  const LOCATION_ROW = useMemo(() => {
    if (isInlineVisit) {
      return makeLocationRow(location);
    }
  }, [location, isInlineVisit]);

  const DATE_AND_TIME_ROW = useMemo(() => {
    if (isInlineVisit) {
      return makeDateAndTimeRow(time);
    }
  }, [time, isInlineVisit]);

  const visitTypeRowsMap = new Map([
    [isInlineVisit, [LOCATION_ROW, REASON_ROW, DATE_AND_TIME_ROW]],
    [isEirHotMySpotVisit, [DATE_ROW, PROVIDER_ROW, REASON_ROW, CONSENT_ROW]],
    [isEirTelemedicineVisit, [DATE_ROW, REASON_ROW, CONSENT_ROW]],
  ]);

  const defaultRows = [
    DATE_ROW,
    PROVIDER_ROW,
    REASON_ROW,
    CONSENT_ROW,
    PAYMENT_ROW,
  ];

  const selectedRows = visitTypeRowsMap.get(true) || defaultRows;

  return { rows: selectedRows };
}



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentOverview/useOverviewRows.js